import fetch from 'isomorphic-fetch'
import * as R from 'ramda'
import axios from 'axios'
import _ from 'lodash'

import constants from 'config/constants'
import auth from '../auth'
import { getHistory } from 'libs/history'
import { isNoSurveyAccessError, notifyNoSurveyAccess } from 'libs/api-errors'

import Promise from 'bluebird'
import { toast } from 'libs/toast'

// skip loading Promise.config in the test environment to avoid potential side effects.
if (process.env.NODE_ENV != 'test') {
  Promise.config({
    cancellation: true,
    warnings: {
      wForgottenReturn: false,
    },
  })
}

if (typeof window === 'undefined') {
  global.Promise = Promise
} else {
  window.Promise = Promise
}

const axiosInstance = axios.create()
const API_URL = constants.backendHost
axiosInstance.defaults.baseURL = `${API_URL}/api/`

export const setDefaultHeaders = (workspaceToken) => {
  axiosInstance.defaults.headers.common['X-Workspace-Token'] = workspaceToken
}

const shouldFetchWithCredentials = Boolean(process.env.REACT_APP_FETCH_WITH_CREDENTIALS)
if (shouldFetchWithCredentials) {
  axiosInstance.defaults.withCredentials = true
}
// Register the response error interceptor once at module load. Registering it
// inside getAxios() (called per request, 34+ call sites) stacked a new
// interceptor on every call, so each handler ran once per accumulated
// interceptor. getAxios() now only refreshes the per-request auth token.
axiosInstance.interceptors.response.use(null, function (error) {
  const jwtErrorMessages = ['Invalid signature.', 'Given token not valid for any token type']
  const isInvalidToken =
    error.response &&
    error.response.status === 401 &&
    jwtErrorMessages.includes(R.prop('detail', error.response?.data))

  if (isInvalidToken) {
    auth.logout()
    getHistory().replace('/login/')
  }

  const isInvalidWorkspaceToken = [
    'No Account matches the given query.',
    'No Company matches the given query.',
  ].includes(R.prop('detail', error.response?.data))
  const isError404 = R.equals(error.response?.status, 404)

  if (isInvalidWorkspaceToken && isError404) {
    toast.error(
      'Oops! This page couldn’t be found. Double-check the URL or use the menu to continue.'
    )
    getHistory().replace('/')
  }

  if (isNoSurveyAccessError(error)) {
    notifyNoSurveyAccess()
  }

  return Promise.reject(error)
})

/**
 * @returns {import('axios').AxiosInstance}
 */
export const getAxios = () => {
  axiosInstance.defaults.headers.common['Authorization'] = `JWT ${auth.getToken()}`
  return axiosInstance
}

const fetchOptions = shouldFetchWithCredentials ? { credentials: 'include' } : {}

export const getRaterData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/rater/`, queryParams)

export const getNpsData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/nps/`, queryParams)

export const getFeedbackData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/feedback/`, queryParams)

export const getReliabilityData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/reliability/`, queryParams)

export const getCustomersData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/customers/`, queryParams)

export const getCustomer = (customerId) =>
  getAxios()
    .get(`customers/${customerId}/`)
    .catch((error) => {
      console.error('[API](getCustomer) failed to run')
      throw error
    })

export const getAnswersData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/dashboard/answers/`, queryParams)

export const getLeaderboard = (surveyToken, queryParams) =>
  getAxios().get(`survey/survey/${surveyToken}/leaderboard/`, {
    params: queryParams,
  })

export const getZendeskAuthenticationData = (queryParams) =>
  fetchWithToken(`${API_URL}/api/zendesk/authentication/`, queryParams)

export const getSurveyList = () =>
  fetchWithToken(`${API_URL}/api/account/survey/?limit=5000&order_by=asc`)

export const getSurveyFromId = (surveyId) =>
  fetchWithToken(`${API_URL}/api/account/survey/${surveyId}/`)

export const updateSurveyFromId = (surveyId, body) =>
  patchFetch(`${API_URL}/api/account/survey/${surveyId}/`, body)

export const updateSurveyFromIdAndThrowIfError = (surveyId, body) =>
  getAxios()
    .patch(`account/survey/${surveyId}/`, body)
    .catch((error) => {
      console.error('Failed while updating survey settings')

      throw error
    })

export const getUserProfile = () => fetchWithToken(`${API_URL}/api/account/users/me/v2/`)

export const updateUserProfile = (body) => patchFetch(`${API_URL}/api/account/users/me/v2/`, body)

export const updateAnswerDetail = (body) =>
  patchFetch(`${API_URL}/api/dashboard/feedback/mark_as_testimonial/`, body)

export const postLogin = (credential) =>
  postFetch(`${API_URL}/api/login/`, credential)
    .then((response) => response.json())
    .catch((e) => {
      console.error('error while logging in', e)
      return e
    })

export const getDemoLogin = () =>
  getFetch(`${API_URL}/api/account/demo/`)
    .then((response) => response.json())
    .catch((e) => {
      console.error('error while logging in to demo account', e)
      return e
    })

export const postResetPassword = (body) => postFetch(`${API_URL}/api/password/reset/`, body)

export const postResetPasswordConfirm = (body) =>
  postFetch(`${API_URL}/api/password/reset/confirm/${body.encoded_token}/`, body)

export const createUser = (userInfo) =>
  postFetch(`${API_URL}/api/account/signup/`, userInfo)
    .then((res) => {
      if (res.status >= 400) {
        return res
      } else {
        return res.json()
      }
    })
    .catch((e) => {
      console.error('error while signing up', e)
      return e
    })

export const getUsers = () =>
  getAxios()
    .get('account/accounts/', {
      params: {
        limit: 100,
      },
    })
    .catch((error) => {
      console.error('[API](getUsers) failed to run')
      throw error
    })

export const getZendeskEndUserCredential = async () => {
  try {
    const data = await fetchWithToken(`${API_URL}/api/zendesk/end-user-authentication/`)
    return data.json()
  } catch (e) {
    console.error('Error while getting Zendesk end user data', e)
  }
}

export const postZendeskEndUserAuthentication = async (credential) => {
  try {
    const response = await postFetchWithToken(
      `${API_URL}/api/zendesk/end-user-authentication/`,
      credential
    )
    const data = await response.json()
    if (data.error) {
      throw new Error(data.error)
    }
    return data
  } catch (error) {
    console.error('Failed while authenticating Zendesk end user', error)
    return error
  }
}

export const postZendeskAuthentication = async (credential) => {
  try {
    const data = await postFetchWithToken(`${API_URL}/api/zendesk/authentication/`, credential)
    return data.json()
  } catch (e) {
    console.error('error while authenticate zendesk', e)
    throw e
  }
}

export const deleteZendeskSatisfactionSync = (queryParams) =>
  deleteFetchWithToken(`${API_URL}/api/zendesk/sync-satisfaction-rating/`, queryParams)

export const createSurvey = async (surveyType) => {
  try {
    const data = await postFetchWithToken(`${API_URL}/api/account/survey/`, surveyType)
    return data
  } catch (e) {
    console.error('error while creating data')
    throw e
  }
}

export const getConnectwiseCredential = () =>
  getAxios()
    .get('connectwise/connectwise-authentication/')
    .catch((error) => {
      console.error('error while getting ConnectWise credential')

      throw error
    })

export const getAutotaskCredential = () => {
  return getAxios()
    .get('autotask/authentication/')
    .catch((error) => {
      console.error('error while getting Autotask credential')
      throw error
    })
}

export const saveOrUpdateConnectwiseCredential = (credential) =>
  getAxios()
    .post('connectwise/connectwise-authentication/', credential)
    .catch((error) => {
      console.error('error while saving or updating ConnectWise credential')

      throw error
    })

export const saveOrUpdateAutotaskCredential = (credential) => {
  return getAxios()
    .post('autotask/authentication/', credential)
    .catch((error) => {
      console.error('error while saving or updating ConnectWise credential')
      throw error
    })
}

export const deleteConnectwiseCredential = () =>
  getAxios()
    .delete('connectwise/connectwise-authentication/')
    .catch((error) => {
      console.error('[API](deleteConnectwiseCredential) failed to run')

      throw error
    })

export const deleteAutotaskCredential = () => {
  return getAxios()
    .delete('autotask/authentication/')
    .catch((error) => {
      console.error('[API](deleteAutotaskCredential) failed to run')
      throw error
    })
}

const getFetch = (url) =>
  fetch(url, {
    method: 'GET',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
    },
  })

const postFetch = (url, body) =>
  fetch(url, {
    method: 'POST',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

const postFetchWithToken = (url, body) =>
  fetch(url, {
    method: 'POST',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'JWT ' + auth.getToken(),
      'X-Workspace-Token': axiosInstance.defaults.headers.common['X-Workspace-Token'],
    },
    body: JSON.stringify(body),
  })

export const updateAccountSettings = (body, companyId) => {
  return fetch(`${API_URL}/api/account/company/${companyId}/`, {
    method: 'PATCH',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'JWT ' + auth.getToken(),
      'X-Workspace-Token': axiosInstance.defaults.headers.common['X-Workspace-Token'],
    },
    body: JSON.stringify(body),
  })
}

export const getAccountSettingInfo = async () => {
  const companyId = await getCompanyId()

  return await fetch(`${API_URL}/api/account/company/${companyId}/`, {
    method: 'GET',
    ...fetchOptions,
    headers: {
      Authorization: 'JWT ' + auth.getToken(),
      'X-Workspace-Token': axiosInstance.defaults.headers.common['X-Workspace-Token'],
    },
  })
}

export const getCompanyId = async () => {
  const res = await fetch(`${API_URL}/api/account/workspaces/`, {
    method: 'GET',
    ...fetchOptions,
    headers: {
      Authorization: 'JWT ' + auth.getToken(),
    },
  })
  if (!res.ok) {
    return undefined
  }
  const data = await res.json().catch(() => ({}))
  const results = data?.results ?? []

  const workspace = results.filter((item) => {
    return item.workspace_token == axiosInstance.defaults.headers.common['X-Workspace-Token']
  })

  const companyId = R.prop('id', workspace[0])
  return companyId
}

export const getFeedbackSearchFilterResult = async (queryParams) => {
  try {
    return await postFetchWithToken(`${API_URL}/api/dashboard/answers/search/`, queryParams)
  } catch (e) {
    console.error('Failed while getting feedback search and filter', e)
  }
}

export const getFeedbackAutoComplete = async (queryParams) => {
  try {
    const resp = await fetchWithToken(
      `${API_URL}/api/dashboard/feedback/autocomplete/`,
      queryParams
    )
    return await resp.json()
  } catch (e) {
    throw new Error(e)
  }
}

export const getEmailSubscription = () =>
  getAxios()
    .get('account/email-subscription/')
    .catch((error) => {
      console.error('Failed while getting notification frequency')

      throw error
    })

export const updateNotificationFrequency = (emailSubscriptionId, body) =>
  getAxios()
    .patch(`account/email-subscription/${emailSubscriptionId}/`, body)
    .catch((error) => {
      console.error('Failed while updating notification frequency')

      throw error
    })

export const updateEmailSurveySubscription = (emailSurveySubscriptionId, body) =>
  getAxios()
    .patch(`account/email-survey-subscription/${emailSurveySubscriptionId}/`, body)
    .catch((error) => {
      console.error('Failed while updating survey email notification configuration')
      throw error
    })

const patchFetch = (url, body) => {
  const request = {
    method: 'PATCH',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }
  return fetchWithToken(url, {}, request)
}

const deleteFetchWithToken = (url, queryParams) =>
  fetch(url + transformQueryParamsObjectToString(queryParams), {
    method: 'DELETE',
    ...fetchOptions,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'JWT ' + auth.getToken(),
      'X-Workspace-Token': axiosInstance.defaults.headers.common['X-Workspace-Token'],
    },
  })

const fetchWithToken = (url, queryParams, request = {}) => {
  const withToken = {
    headers: {
      Authorization: 'JWT ' + auth.getToken(),
      'X-Workspace-Token': axiosInstance.defaults.headers.common['X-Workspace-Token'],
    },
  }

  return fetch(
    url + transformQueryParamsObjectToString(queryParams),
    _.merge({}, fetchOptions, request, withToken)
  )
}

export function transformQueryParamsObjectToString(queryParams) {
  queryParams = queryParams || {}
  const queryString = Object.keys(queryParams)
    .filter((key) => {
      const value = queryParams[key]
      return value !== false && !R.isNil(value) && !R.isEmpty(value)
    })
    .map((key) => `${key}=${queryParams[key]}`)
    .join('&')

  return queryString ? `?${queryString}` : ''
}

export const uploadImportCustomersCSV = (formData) => {
  return getAxios()({
    method: 'post',
    url: `${API_URL}/api/customers/import-customers/`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}

export const getResponsesFilter = (body, params = {}) =>
  getAxios()
    .post('responses/', body, { params })
    .catch((error) => {
      console.error('[API](getResponsesFilter) failed to run')
      throw error
    })

export const deleteSession = (sessionId) => {
  return getAxios()
    .delete(`responses/${sessionId}/`)
    .catch((error) => {
      console.error('[API](deleteSession) failed to run')
      throw error
    })
}

const deleteFetchWithData = (url, body) => {
  const request = {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }
  return fetchWithToken(url, {}, request)
}

export const removeTag = (sessionId, tagId) => {
  const body = {
    tag: tagId,
  }
  return deleteFetchWithData(`${API_URL}/api/responses/${sessionId}/tag/`, body)
}

export const deleteTag = (tagId) => deleteFetchWithToken(`${API_URL}/api/responses/tag/${tagId}`)

/**
 * @typedef {object} Tag
 * @property {number} id
 * @property {string} name
 * @property {string} color
 *
 * @param {Tag} tag
 */
export const updateTagInfo = (tag) => {
  const body = {
    name: tag.name,
    color: tag.color,
  }
  return patchFetch(`${API_URL}/api/responses/tag/${tag.id}/`, body)
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/api/index.js